import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Laptop } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import {
  AssetView,
  AssetCategoryView,
  AssetStatsView,
  AssetStatus,
  AssetCondition,
  AssetListQuery,
  CreateAssetInput,
  UpdateAssetInput,
  IssueAssetInput,
  ReturnAssetInput,
  getAssets,
  getAsset,
  createAsset,
  updateAsset,
  archiveAsset,
  getAssetStats,
  getCategories,
  issueAsset,
  returnAsset,
  EquipmentCoverageView,
  getEquipmentCoverage,
  getAssetSettings,
} from "../../../api/General/assets";
import Button from "@/components/ui/Button";
import AlertDialog from "@/components/ui/AlertDialog";
import AssetStatsCards from "./components/AssetStatsCards";
import AssetFilters from "./components/AssetFilters";
import AssetTable from "./components/AssetTable";
import AssetFormModal from "./components/AssetFormModal";
import AssetDetailsModal from "./components/AssetDetailsModal";
import IssueAssetModal from "./components/IssueAssetModal";
import ReturnAssetModal from "./components/ReturnAssetModal";
import BulkImportModal from "./components/BulkImportModal";
import AssetRequestsTab from "./components/AssetRequestsTab";
import EquipmentCoverageTab from "./components/EquipmentCoverageTab";
import Pagination from "@/components/Pagination";

export default function AssetsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<"assets" | "requests" | "coverage">("assets");
  const [coverageSummary, setCoverageSummary] = useState<EquipmentCoverageView | null>(null);
  const [requireOtp, setRequireOtp] = useState(true);
  const [searchParams] = useSearchParams();

  // Data state
  const [assets, setAssets] = useState<AssetView[]>([]);
  const [stats, setStats] = useState<AssetStatsView | null>(null);
  const [categories, setCategories] = useState<AssetCategoryView[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Loading state
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Filter state
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<number | undefined>();
  const [statusFilter, setStatusFilter] = useState<AssetStatus | undefined>();
  const [conditionFilter, setConditionFilter] = useState<AssetCondition | undefined>();
  const [sortBy, setSortBy] = useState("");
  const [sortDescending, setSortDescending] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [reloadNonce, setReloadNonce] = useState(0);

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<AssetView | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<AssetView | null>(null);
  const [issuingAsset, setIssuingAsset] = useState<AssetView | null>(null);
  const [returningAsset, setReturningAsset] = useState<AssetView | null>(null);
  const [issueSaving, setIssueSaving] = useState(false);
  const [returnSaving, setReturnSaving] = useState(false);
  const [archiveTargetId, setArchiveTargetId] = useState<number | null>(null);
  const [archiving, setArchiving] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Search debounce
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      const result = await getCategories();
      if (result.errors.length === 0) {
        setCategories(result.payload);
      }
    } catch {
      // silent
    }
  }, []);

  // Load stats
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const result = await getAssetStats();
      if (result.errors.length === 0) {
        setStats(result.payload);
      }
    } catch {
      // silent
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Load coverage badge summary (so the tab shows a count before activation)
  const loadCoverageSummary = useCallback(async () => {
    try {
      const result = await getEquipmentCoverage();
      if (result.errors.length === 0) setCoverageSummary(result.payload);
    } catch {
      // silent
    }
  }, []);

  // Load assets
  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const query: AssetListQuery = {
        search: debouncedSearch || undefined,
        categoryId: categoryFilter,
        status: statusFilter,
        condition: conditionFilter,
        sortBy: sortBy || undefined,
        sortDescending,
        page,
        pageSize,
      };
      const result = await getAssets(query);
      if (result.errors.length === 0) {
        setAssets(result.payload.items);
        setTotalCount(result.payload.totalCount);
        setTotalPages(result.payload.totalPages);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, categoryFilter, statusFilter, conditionFilter, sortBy, sortDescending, page, pageSize, reloadNonce]);

  // Initial load
  useEffect(() => {
    loadCategories();
    loadStats();
    loadCoverageSummary();
  }, [loadCategories, loadStats, loadCoverageSummary]);

  // Honor a ?tab=coverage deep-link from the Q2 drill-down (run once on mount)
  useEffect(() => {
    const tab = searchParams.get("tab");
    const valid = ["assets", "requests", "coverage"];
    if (tab && valid.includes(tab)) setActiveTab(tab as typeof activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load OTP setting once on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await getAssetSettings();
        if (r.errors.length === 0) setRequireOtp(r.payload.requireAssetIssuanceOtp);
      } catch {
        // keep default (true = require OTP)
      }
    })();
  }, []);

  // Reload assets on filter/page change
  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  // Sort handler
  const handleSort = useCallback((field: string) => {
    if (sortBy === field) {
      setSortDescending((prev) => !prev);
    } else {
      setSortBy(field);
      setSortDescending(false);
    }
    setPage(1);
  }, [sortBy]);

  // Row click -> show details
  const handleRowClick = useCallback(async (asset: AssetView) => {
    try {
      const result = await getAsset(asset.id);
      if (result.errors.length === 0) {
        setSelectedAsset(result.payload);
        setDetailsModalOpen(true);
      }
    } catch {
      setSelectedAsset(asset);
      setDetailsModalOpen(true);
    }
  }, []);

  // Add asset
  const handleAddClick = useCallback(() => {
    setEditingAsset(null);
    setFormModalOpen(true);
  }, []);

  // Edit from details
  const handleEditFromDetails = useCallback((asset: AssetView) => {
    setDetailsModalOpen(false);
    setEditingAsset(asset);
    setFormModalOpen(true);
  }, []);

  // Save (create or update)
  const handleSave = useCallback(async (input: CreateAssetInput | UpdateAssetInput) => {
    setSaving(true);
    try {
      const isUpdate = "id" in input;
      const result = isUpdate ? await updateAsset(input as UpdateAssetInput) : await createAsset(input as CreateAssetInput);

      if (result.errors.length === 0) {
        showToast(isUpdate ? t("assets.assetUpdated") : t("assets.assetCreated"), "success");
        setFormModalOpen(false);
        setEditingAsset(null);
        loadAssets();
        loadStats();
      } else {
        showToast(result.errors[0]?.description || t("Something went wrong"), "error");
      }
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setSaving(false);
    }
  }, [loadAssets, loadStats, showToast, t]);

  // Issue from details
  const handleIssueFromDetails = useCallback((asset: AssetView) => {
    setDetailsModalOpen(false);
    setIssuingAsset(asset);
    setIssueModalOpen(true);
  }, []);

  // Return from details
  const handleReturnFromDetails = useCallback((asset: AssetView) => {
    setDetailsModalOpen(false);
    setReturningAsset(asset);
    setReturnModalOpen(true);
  }, []);

  // Issue asset
  const handleIssue = useCallback(async (input: IssueAssetInput) => {
    setIssueSaving(true);
    try {
      const result = await issueAsset(input);
      if (result.errors.length === 0) {
        showToast(t("assets.assetIssued"), "success");
        setIssueModalOpen(false);
        setIssuingAsset(null);
        loadAssets();
        loadStats();
        loadCoverageSummary();
      } else {
        showToast(result.errors[0]?.description || t("Something went wrong"), "error");
      }
    } catch (e: any) {
      // Re-throw so the modal can show an inline OTP error when relevant
      throw e;
    } finally {
      setIssueSaving(false);
    }
  }, [loadAssets, loadStats, loadCoverageSummary, showToast, t]);

  // Return asset
  const handleReturn = useCallback(async (input: ReturnAssetInput) => {
    setReturnSaving(true);
    try {
      const result = await returnAsset(input);
      if (result.errors.length === 0) {
        showToast(t("assets.assetReturned"), "success");
        setReturnModalOpen(false);
        setReturningAsset(null);
        loadAssets();
        loadStats();
        loadCoverageSummary();
      } else {
        showToast(result.errors[0]?.description || t("Something went wrong"), "error");
      }
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setReturnSaving(false);
    }
  }, [loadAssets, loadStats, loadCoverageSummary, showToast, t]);

  // Refresh action in the search bar: clear search + all dropdowns and force a reload
  const handleResetFilters = useCallback(() => {
    setSearch("");
    setDebouncedSearch("");
    setCategoryFilter(undefined);
    setStatusFilter(undefined);
    setConditionFilter(undefined);
    setPage(1);
    setReloadNonce((n) => n + 1);
  }, []);

  // Fix action from the coverage tab: jump to the Assets tab, filtered to available stock.
  // Needed equipment is an asset CATEGORY, so filter by that category (a name search would
  // miss assets whose name doesn't contain the category). Fall back to a name search for any
  // legacy free-text needed item that doesn't match a category.
  const handleIssueForItem = useCallback((neededItem: string) => {
    setActiveTab("assets");
    setStatusFilter(AssetStatus.Available);
    setPage(1);
    const cat = categories.find((c) => c.name.toLowerCase() === neededItem.toLowerCase());
    if (cat) {
      setCategoryFilter(cat.id);
      setSearch("");
      setDebouncedSearch("");
    } else {
      setSearch(neededItem);
      setCategoryFilter(undefined);
    }
  }, [categories]);

  // Archive - open the confirmation dialog; the actual call happens in confirmArchive
  const handleArchive = useCallback((id: number) => {
    setArchiveTargetId(id);
  }, []);

  const confirmArchive = useCallback(async () => {
    if (archiveTargetId == null) return;
    setArchiving(true);
    try {
      const result = await archiveAsset(archiveTargetId);
      if (result.errors.length === 0) {
        showToast(t("assets.assetArchived"), "success");
        setDetailsModalOpen(false);
        setSelectedAsset(null);
        loadAssets();
        loadStats();
      } else {
        showToast(result.errors[0]?.description || t("Something went wrong"), "error");
      }
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setArchiving(false);
      setArchiveTargetId(null);
    }
  }, [archiveTargetId, loadAssets, loadStats, showToast, t]);

  return (
    <PageLayout
      title={t("assets.title")}
      description={t("assets.subtitle")}
      icon={Laptop}
      accentColor="amber"
      maxWidth="1600px"
      headerActions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setBulkImportOpen(true)}
            data-testid="bulk-import-btn"
          >
            <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            {t("assets.bulkImport")}
          </Button>
          <Button
            variant="outline"
            onClick={handleAddClick}
            data-testid="add-asset-btn"
          >
            <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t("assets.addAsset")}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">

      {/* Tabs */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setActiveTab("assets")}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
            activeTab === "assets"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
          }`}
          data-testid="assets-tab"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          {t("assets.assetsTab")}
        </button>
        <button
          onClick={() => setActiveTab("requests")}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
            activeTab === "requests"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
          }`}
          data-testid="requests-tab"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          {t("assetRequests.requestsTab")}
        </button>
        <button
          onClick={() => setActiveTab("coverage")}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
            activeTab === "coverage"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
          }`}
          data-testid="coverage-tab"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          {t("assets.coverageTab")}
          {coverageSummary && coverageSummary.underCoveredCount > 0 && (
            <span className="ms-1 inline-flex items-center justify-center rounded-full bg-amber-100 text-amber-700 text-xs font-semibold px-1.5 min-w-[1.25rem] h-5">
              {coverageSummary.underCoveredCount}
            </span>
          )}
        </button>
      </div>

      {activeTab === "assets" ? (
        <>
          {/* Stats */}
          <AssetStatsCards stats={stats} loading={statsLoading} />

          {/* Filters */}
          <AssetFilters
            search={search}
            onSearchChange={setSearch}
            categoryId={categoryFilter}
            onCategoryChange={(val) => { setCategoryFilter(val); setPage(1); }}
            status={statusFilter}
            onStatusChange={(val) => { setStatusFilter(val); setPage(1); }}
            condition={conditionFilter}
            onConditionChange={(val) => { setConditionFilter(val); setPage(1); }}
            categories={categories}
            onReset={handleResetFilters}
          />

          {/* Table */}
          <AssetTable
            assets={assets}
            loading={loading}
            onRowClick={handleRowClick}
            sortBy={sortBy}
            sortDescending={sortDescending}
            onSort={handleSort}
          />

          {/* Pagination */}
          {totalCount > 0 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              variant="compact"
              pageSize={pageSize}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
              totalCount={totalCount}
            />
          )}
        </>
      ) : activeTab === "requests" ? (
        <AssetRequestsTab />
      ) : (
        <EquipmentCoverageTab onIssueItem={handleIssueForItem} />
      )}

      {/* Form Modal (Add / Edit) */}
      <AssetFormModal
        open={formModalOpen}
        onClose={() => { setFormModalOpen(false); setEditingAsset(null); }}
        onSave={handleSave}
        asset={editingAsset}
        categories={categories}
        saving={saving}
      />

      {/* Details Modal */}
      <AssetDetailsModal
        open={detailsModalOpen}
        onClose={() => { setDetailsModalOpen(false); setSelectedAsset(null); }}
        asset={selectedAsset}
        onEdit={handleEditFromDetails}
        onArchive={handleArchive}
        onIssue={handleIssueFromDetails}
        onReturn={handleReturnFromDetails}
      />

      {/* Issue Asset Modal */}
      <IssueAssetModal
        open={issueModalOpen}
        onClose={() => { setIssueModalOpen(false); setIssuingAsset(null); }}
        asset={issuingAsset}
        onIssue={handleIssue}
        issuing={issueSaving}
        requireOtp={requireOtp}
      />

      {/* Return Asset Modal */}
      <ReturnAssetModal
        open={returnModalOpen}
        onClose={() => { setReturnModalOpen(false); setReturningAsset(null); }}
        asset={returningAsset}
        onReturn={handleReturn}
        returning={returnSaving}
      />

      {/* Bulk Import Modal */}
      <BulkImportModal
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onImportComplete={() => { loadAssets(); loadStats(); }}
      />

      {/* Archive Confirmation */}
      <AlertDialog
        open={archiveTargetId !== null}
        onOpenChange={(open) => { if (!open) setArchiveTargetId(null); }}
        title={t("assets.archive")}
        description={t("assets.confirmArchive")}
        variant="destructive"
        confirmLabel={t("assets.archive")}
        cancelLabel={t("common.cancel")}
        isLoading={archiving}
        onConfirm={confirmArchive}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 end-6 z-[60] px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === "success" ? "bg-success/10 text-success border border-success/20" : "bg-destructive/10 text-destructive border border-destructive/20"
          }`}
        >
          {toast.message}
        </div>
      )}
      </div>
    </PageLayout>
  );
}
